import React, { useEffect, useState } from 'react'
import { Slider, SliderThumb, SliderTrack } from 'react-aria-components'

import { useCurrencySymbol } from 'src/lib/js/hooks/useFormatPrice'
import { t } from 'src/translations'

type Props = {
  bounds: { min: number; max: number }
  value: { min: number; max: number }
  onChange: (value: { min: number; max: number }) => void
  // id of a visible label element to associate with the slider for a11y.
  labelledById: string
}

const clamp = (n: number, lo: number, hi: number) =>
  Math.min(Math.max(n, lo), hi)

export const PriceRangeSlider = ({
  bounds,
  value,
  onChange,
  labelledById
}: Props) => {
  const currencySymbol = useCurrencySymbol()
  // The slider and inputs operate on whole dollars only. Round the min bound
  // down and the max bound up so the rounded range fully covers the actual one.
  const roundedBounds = {
    min: Math.floor(bounds.min),
    max: Math.ceil(bounds.max)
  }
  // Display whole dollars only: floor the min and ceil the max so the shown
  // range never falls inside the actual selected range.
  const roundedValue = {
    min: Math.floor(value.min),
    max: Math.ceil(value.max)
  }
  // Text box values — hold freeform strings while the user is mid-type.
  const [minInput, setMinInput] = useState<string>(String(roundedValue.min))
  const [maxInput, setMaxInput] = useState<string>(String(roundedValue.max))
  // Thumb positions — updated on every drag frame; query fires only on release.
  const [sliderMinValue, setSliderMinValue] = useState(roundedValue.min)
  const [sliderMaxValue, setSliderMaxValue] = useState(roundedValue.max)
  const stacked = sliderMinValue >= sliderMaxValue

  useEffect(() => {
    setMinInput(String(roundedValue.min))
  }, [roundedValue.min])

  useEffect(() => {
    setMaxInput(String(roundedValue.max))
  }, [roundedValue.max])

  // Sync thumbs when an external change resets value (e.g. clear filters).
  useEffect(() => {
    setSliderMinValue(roundedValue.min)
    setSliderMaxValue(roundedValue.max)
  }, [roundedValue.min, roundedValue.max])

  // Keeps thumbs and text inputs in sync while dragging, without firing a query.
  const handleSliderChange = (next: number | number[]) => {
    const [min, max] = next as [number, number]
    setSliderMinValue(Math.round(min))
    setSliderMaxValue(Math.round(max))
    setMinInput(String(Math.round(min)))
    setMaxInput(String(Math.round(max)))
  }

  // Fires the query only when the user releases the thumb.
  const handleSliderChangeEnd = (next: number | number[]) => {
    const [min, max] = next as [number, number]
    onChange({ min: Math.round(min), max: Math.round(max) })
  }

  const commit = (side: 'min' | 'max') => {
    const draft = side === 'min' ? minInput : maxInput
    const parsed = parseFloat(draft)
    if (Number.isNaN(parsed)) {
      const reset = side === 'min' ? setMinInput : setMaxInput
      reset(String(roundedValue[side]))
      return
    }
    const low = side === 'min' ? roundedBounds.min : sliderMinValue
    const high = side === 'min' ? sliderMaxValue : roundedBounds.max
    const next = Math.round(clamp(parsed, low, high))
    onChange({ min: sliderMinValue, max: sliderMaxValue, [side]: next })
  }

  return (
    <div className='priceRangeSlider'>
      <Slider
        className='sliderContainer'
        aria-labelledby={labelledById}
        minValue={roundedBounds.min}
        maxValue={roundedBounds.max}
        value={[sliderMinValue, sliderMaxValue]}
        onChange={handleSliderChange}
        onChangeEnd={handleSliderChangeEnd}
      >
        <SliderTrack className='sliderTrack'>
          {({ state }) => (
            <>
              <div className='trackBase'>
                <div
                  className='trackFill'
                  style={
                    {
                      '--start': `${state.getThumbPercent(0) * 100}%`,
                      '--size': `${
                        (state.getThumbPercent(1) - state.getThumbPercent(0)) *
                        100
                      }%`
                    } as React.CSSProperties
                  }
                />
              </div>
              <SliderThumb
                className='sliderThumb'
                index={0}
                aria-label={t('filters.price-min')}
                style={{
                  // When the thumbs overlap, keep whichever one still has room
                  // to move on top. Pinned at the min bound, only the max thumb
                  // can move (right), so it wins; otherwise the min thumb wins.
                  zIndex: stacked && sliderMinValue > roundedBounds.min ? 1 : 0
                }}
              />
              <SliderThumb
                className='sliderThumb'
                index={1}
                aria-label={t('filters.price-max')}
                style={{
                  zIndex: stacked && sliderMinValue <= roundedBounds.min ? 1 : 0
                }}
              />
            </>
          )}
        </SliderTrack>
      </Slider>
      <div className='priceInputs'>
        <PriceInput
          prefix={currencySymbol}
          value={minInput}
          min={roundedBounds.min}
          max={sliderMaxValue}
          onChange={setMinInput}
          onCommit={() => commit('min')}
          ariaLabel={t('filters.price-min')}
        />
        <span className='priceInputSeparator'>
          {t('filters.price-separator')}
        </span>
        <PriceInput
          prefix={currencySymbol}
          value={maxInput}
          min={sliderMinValue}
          max={roundedBounds.max}
          onChange={setMaxInput}
          onCommit={() => commit('max')}
          ariaLabel={t('filters.price-max')}
        />
      </div>
    </div>
  )
}

type PriceInputProps = {
  prefix: string
  value: string
  min: number
  max: number
  onChange: (value: string) => void
  onCommit: () => void
  ariaLabel: string
}

const PriceInput = ({
  prefix,
  value,
  min,
  max,
  onChange,
  onCommit,
  ariaLabel
}: PriceInputProps) => (
  <label className='priceInput'>
    <span className='priceInputPrefix'>{prefix}</span>
    <input
      type='number'
      inputMode='numeric'
      value={value}
      min={min}
      max={max}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onCommit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
      }}
      aria-label={ariaLabel}
    />
  </label>
)
